import { type CSSProperties, useMemo } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import {
  useActiveTabState,
  useTabState,
} from "@features/sales-v3/hooks/use-tab-state";
import { type SalesTableVisualRow } from "@features/sales-v3/types/table";
import {
  type BenchmarkIndicatorRow,
  useUniversalBenchmarksRow,
} from "@features/universal-benchmarks";

import {
  FIXED_NAME_COLUMN_ID,
  FIXED_STATUS_COLUMN_ID,
  SELECTION_COLUMN_WIDTH,
} from "../constants";
import { MetricDataFormatter } from "../data-type-formatters";
import { BenchmarksIndicator } from "../data-type-formatters/benchmarks-indicator";

// This is a simplification of what the actual type injected into the component by @devexpress is.
type Cell = {
  colSpan: number;
  column: {
    type?: unknown;
    column: {
      id: string;
      compareDataLoading: boolean;
      name: string;
      title: string;
    };
  };
};

export interface CustomSalesTableHeadWithFixedRowProps {
  readonly isFixed: boolean;
  readonly children: JSX.Element[];
  className?: string;
  style?: CSSProperties;
}

export const CustomSalesTableHeadWithFixedRow =
  (
    tableSummary: SalesTableVisualRow,
    expandedRowIds: (string | number)[],
    onCollapseAllRows: () => void,
    isCompacted: boolean,
    rows: SalesTableVisualRow[],
  ) =>
  ({
    isFixed,
    className,
    style,
    children,
  }: CustomSalesTableHeadWithFixedRowProps) => {
    const columnsInView: Cell[] = children[0].props.cells;

    return (
      <thead
        className={clsx(
          {
            "dx-g-bs4-fixed-header": isFixed,
            "dx-g-bs4-table-sticky": isFixed,
            "bg-white": isFixed,
          },
          className,
        )}
        style={style}
      >
        {children}
        <tr>
          {columnsInView.map(({ column }: Cell, index: number) => (
            <ColumnHead
              key={index}
              isCompacted={isCompacted}
              column={column}
              tableSummary={tableSummary}
              expandedRowIds={expandedRowIds}
              onCollapseAllRows={onCollapseAllRows}
              rows={rows}
            />
          ))}
        </tr>
      </thead>
    );
  };

const ColumnHead = ({
  column,
  isCompacted,
  tableSummary,
  expandedRowIds,
  onCollapseAllRows,
  rows,
}: {
  column: Cell["column"];
  isCompacted: boolean;
  tableSummary: SalesTableVisualRow;
  expandedRowIds: (string | number)[];
  onCollapseAllRows: () => void;
  rows: SalesTableVisualRow[];
}) => {
  const { user } = useUser();
  const { filters, attributionModel, attributionWindow, accountingMode } =
    usePageConfiguration();
  const { tabState: breakdownTabState } = useTabState("breakdown");
  const { isBreakdownTab } = useActiveTabState();

  const benchmarkFilters = useMemo(() => {
    const benchmarkFilters = [];

    for (const filter of filters) {
      for (const value of filter.values) {
        benchmarkFilters.push({ breakdown: filter.key, value });
      }
    }

    if (!isBreakdownTab) {
      for (const row of breakdownTabState.proposedFiltersState
        .inclusionAtomicities) {
        try {
          for (const atomicity of JSON.parse(row.atomicity)) {
            for (const [breakdown, value] of Object.entries(atomicity)) {
              benchmarkFilters.push({ breakdown, value: value as string });
            }
          }
          // eslint-disable-next-line no-empty
        } catch {}
      }
    }

    return benchmarkFilters;
  }, [filters, breakdownTabState]);

  const universalBenchmarks = useUniversalBenchmarksRow({
    level:
      (tableSummary?.rowMetadata?.tab === "breakdown"
        ? "campaign"
        : tableSummary?.rowMetadata?.tab) ?? "campaign",
    attributionModel,
    attributionWindow,
    accountingMode,
    breakdowns: [],
    filters: benchmarkFilters,
  });

  const benchmarkKpis = useMemo(() => {
    if (!tableSummary) {
      return [];
    }

    if (!universalBenchmarks) {
      return [];
    }

    const spendValue = tableSummary.export?.["spend"];

    if (!spendValue) {
      return [];
    }

    const rv: BenchmarkIndicatorRow[] = [];

    universalBenchmarks.benchmarks.forEach((bench) => {
      const metric = bench.metric;

      if (!metric?.metadata.salesTableMetricId) {
        return;
      }

      rv.push({
        metricId: metric.metadata.salesTableMetricId,
        name:
          metric.name ??
          metric.metadata.name ??
          metric.spec?.name ??
          metric.metadata.salesTableMetricId,
        current: tableSummary.export?.[metric.metadata.salesTableMetricId],
        target: bench.value,
        format: metric.format,
        positiveTrend: metric.isPositiveChangeGood ? "up" : "down",
      });
    });

    return rv;
  }, [tableSummary, user.currencies.currentCurrency, universalBenchmarks]);

  // Not sure why but there's s stub header column
  const columnType = column?.type?.toString();

  if (columnType && ["Symbol(stub)", "Symbol(flex)"].includes(columnType)) {
    return <th className="p-0 table-summary-cell" scope="col"></th>;
  }

  if (columnType === "Symbol(select)") {
    return (
      <th
        className="table-summary-cell left-0 p-0 dx-g-bs4-fixed-cell position-sticky z-[1]"
        scope="col"
      ></th>
    );
  }

  // Only a known data column would have a column property
  if (!column?.column) {
    return null;
  }

  if (column.column.name === FIXED_NAME_COLUMN_ID) {
    return (
      <th
        className="table-summary-cell position-sticky z-[1]"
        style={{ left: `${SELECTION_COLUMN_WIDTH}px` }}
        scope="col"
      >
        <div className="flex items-center h-full px-3">
          <div className="flex flex-col justify-center leading-3">
            <div className="flex items-center gap-2">
              <div>Total / AVG</div>
              <BenchmarksIndicator
                benchmarkItemName="Total"
                universalBenchmarks={universalBenchmarks}
                benchmarkIndicatorRows={benchmarkKpis}
              />
            </div>
            {expandedRowIds.length > 0 && (
              <button
                className={clsx("font-normal text-[0.65rem] text-cosmic", {
                  "mt-2": !isCompacted,
                  "mt-0.5": isCompacted,
                })}
                onClick={() => onCollapseAllRows()}
              >
                <i className="fa-regular fa-sharp fa-arrows-to-line mr-1"></i>
                Collapse all rows
              </button>
            )}
          </div>
        </div>
      </th>
    );
  }

  if (column.column.name === FIXED_STATUS_COLUMN_ID) {
    return <th className="table-summary-cell z-[1]" scope="col"></th>;
  }

  return (
    <th key={column.column.id} className="table-summary-cell z-1" scope="col">
      <MetricDataFormatter
        row={tableSummary}
        column={column.column}
        value={null}
        isSummaryRow={true}
      />
    </th>
  );
};
